import * as Popover from '@radix-ui/react-popover';
import { XCloseIcon } from '@vizit/icons';
import { cx } from '@vizit/theme';
import * as React from 'react';
import { Button } from '../button';
import { ButtonLevel, ButtonSize } from '../button/types';

/**
 * Props for the Banner component
 */
interface BannerProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Additional CSS classes for the dismiss button
   *
   * @optional
   * @type string
   */
  buttonClassName?: string;
  /**
   * The anchor element that the banner will be positioned relative to
   *
   * @type React.ReactNode
   */
  children: React.ReactNode;
  /**
   * Additional CSS classes
   *
   * @optional
   * @type string
   */
  className?: string;
  /**
   * Whether the banner is open
   *
   * @optional
   * @default true
   * @type boolean
   */
  isOpen?: boolean;
  /**
   * Whether to show the dismiss button
   *
   * @optional
   * @default true
   * @type boolean
   */
  showDismiss?: boolean;
  /**
   * Callback fired when the banner is dismissed
   *
   * @optional
   * @type () => void
   */
  onDismiss?: () => void;
}

interface BannerAnchorProps extends Omit<React.ComponentProps<typeof Popover.Anchor>, 'asChild'> {
  /**
   * The anchor element that the banner will be positioned relative to
   *
   * @type React.ReactNode
   */
  children: React.ReactNode;
}

interface BannerContentProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * The content to display in the banner
   *
   * @type React.ReactNode
   */
  children: React.ReactNode;
}

function BannerAnchor(props: BannerAnchorProps) {
  const { children, ...rest } = props;
  return (
    <Popover.Anchor asChild {...rest}>
      {children}
    </Popover.Anchor>
  );
}

function BannerContent(props: BannerContentProps) {
  const { children, className } = props;
  return <div className={cx('flex-1 flex items-center', className)}>{children}</div>;
}

function Banner(props: BannerProps) {
  const {
    buttonClassName,
    children: childrenProp,
    className,
    isOpen = true,
    showDismiss = true,
    onDismiss,
    ...other
  } = props;

  const content = React.useMemo(
    () =>
      React.Children.toArray(childrenProp).find((child) => React.isValidElement(child) && child.type === BannerContent),
    [childrenProp],
  );
  const anchor = React.useMemo(
    () =>
      React.Children.toArray(childrenProp).find((child) => React.isValidElement(child) && child.type === BannerAnchor),
    [childrenProp],
  );

  const handleOpenChange = React.useCallback(
    (open: boolean) => {
      if (!open) {
        onDismiss?.();
      }
    },
    [onDismiss],
  );

  // Prevents banner closing by default on blur
  const handleCloseOnBlur = React.useCallback(<T extends Event>(e: T) => {
    e.preventDefault();
  }, []);

  return (
    <Popover.Root open={isOpen} onOpenChange={handleOpenChange} modal={false}>
      {anchor}
      <Popover.Portal>
        <Popover.Content
          side="bottom"
          align="start"
          sideOffset={0}
          alignOffset={0}
          onPointerDownOutside={handleCloseOnBlur}
          onInteractOutside={handleCloseOnBlur}
          onEscapeKeyDown={handleCloseOnBlur}
          className={cx(
            'w-screen left-0 right-0',
            'transform transition-all duration-300 ease-in-out outline-none',
            'data-[state=closed]:animate-out data-[state=closed]:slide-out-to-top-2 data-[state=closed]:fade-out-0',
          )}
        >
          {/* The <output> HTML element is a container element into which a site or app can inject the results of a calculation or the outcome of a user action. 
          <output/> has a role="status" which is used to indicate that the content is live and should be updated as the user interacts with the page.
          aria-live="polite" is used to indicate that the content is live and should be updated as the user interacts with the page.
          aria-atomic="true" is used to indicate that the content is live and should be updated as the user interacts with the page.*/}
          <output aria-live="polite" aria-atomic="true">
            <div
              className={cx(
                'w-full min-h-[56px] px-16 py-4 flex items-center justify-between z-50 bg-brand-600 text-primary-on-brand outline-none max-w-screen-sm:min-h-[48px] max-w-screen-sm:px-8 max-w-screen-sm:py-3',
                className,
              )}
              {...other}
            >
              {content}
              {showDismiss && onDismiss && (
                <Popover.Close asChild>
                  <Button
                    level={ButtonLevel['Text-Secondary']}
                    size={ButtonSize.Sm}
                    className={cx(
                      'text-primary-on-brand bg-brand-600 hover:bg-brand-700 hover:text-primary-on-brand',
                      buttonClassName,
                    )}
                    aria-label="Dismiss banner"
                  >
                    <XCloseIcon size={16} aria-hidden="true" />
                  </Button>
                </Popover.Close>
              )}
            </div>
          </output>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

export type { BannerProps, BannerContentProps, BannerAnchorProps };
export { Banner, BannerContent, BannerAnchor };
